import { StreamTypes } from 'store/player/actions.types';
import {
    AssetClientTrickModeControlFlags,
    ClientBlackoutControlFlags,
    ClientRestartControlFlags,
    ClientTimeshiftControlFlags,
    EpgProgram,
    OOHControlFlags,
    PlaybackTypes,
    RBAssetType,
    RBEditorial,
    RBLink,
    RBProgram,
    RBRating,
    RBTechnical,
} from 'store/railsBuilder/reducer.types';
import { RecordingItem } from 'store/recordings/reducer.types';

import { TrickModesByPlaybackType } from 'services/data-manager/parsing/utils/trickModesControl';

import { BitmovinPlayerSourceConfig } from 'components/Player/types.ts';

import { components } from './types.railsbuilder';

export enum RBAssetEntityType {
    NonContent = 'NonContent',
    BtvProgramme = 'BtvProgramme',
    EventItemWithServiceInfo = 'EventItemWithServiceInfo',
    CuratedItem = 'CuratedItem',
    BtvEditorial = 'BtvEditorial',
    CdvrRecording = 'CdvrRecording',
    VodEditorial = 'VodEditorial',
    Series = 'Series',
    DeeplinkRecommendation = 'DeeplinkRecommendation',
    BtvServices = 'BtvServices',
}

export type RailItem = components['schemas']['RailItem'] & {
    entityType?: RBAssetEntityType;
};

export type VodEditorial = components['schemas']['VodEditorial'] &
    RailItem & {
        Ratings?: RBRating[];
        links?: RBLink[];
        type?: RBAssetType;
        playbackType?: PlaybackTypes;
        Subtitles?: string[];
        Audio?: string[];
        SeriesTitle?: string;
        EpisodeTitle?: string;
        technicals?: RBTechnical[];
        NetDuration?: number;
    } & ClientBlackoutControlFlags &
    ClientRestartControlFlags &
    ClientTimeshiftControlFlags &
    AssetClientTrickModeControlFlags &
    OOHControlFlags;
export type BtvEditorial = components['schemas']['BtvEditorial'] &
    RailItem & {
        Ratings?: RBRating[];
        type?: RBAssetType;
    };
export type BtvProgramme = components['schemas']['BtvProgramme'] &
    RailItem & {
        Ratings?: RBRating[];
        links?: RBLink[];
        type?: RBAssetType;
        playbackType?: PlaybackTypes;
        position?: number | undefined;
    };
export type CdvrRecording = components['schemas']['CdvrRecording'] & {
    contentId?: string;
} & RailItem;
export type DeeplinkRecommendation =
    components['schemas']['DeeplinkRecommendation'] & RailItem;
export type EventItemWithServiceInfo =
    components['schemas']['EventItemWithServiceInfo'] & RailItem;

// Inherit STB ContentType rail property
export enum ContentType {
    Gallery = 'Gallery',
    Catchup = 'Catchup',
    NowNext = 'NowNext',
    ChannelItem = 'ChannelItem',
    VOD = 'VOD',
    Mixed = 'Mixed',
    NPVR = 'NPVR',
    ContinueWatching = 'ContinueWatching',
    DetailsEpisode = 'DetailsEpisode', // App side usage only. Used for rendering eposides stripe on the details page
    CastPerson = 'CastPerson', // App side usage only. Used for rendering Cast&Crew stripe on the Details page

    // Search related types.
    SearchProgram = 'SearchProgram', // item type from search programs stripe
    SearchCatchup = 'SearchCatchup', // used only locally(for search) to differ Catchup item from the search results stcu items
    SearchVod = 'SearchVod', // used only locally(for search),
}

export interface NowNextData {
    channelId: string;
    programmes: EpgProgram[];
}

export enum RBEventParentalControlRating {
    HU_GENERAL = 'hu-gy',
    HU_UNRATED = 'hu-unrated',
    HU6 = 'hu-6',
    HU12 = 'hu-12',
    HU16 = 'hu-16',
    HU18 = 'hu-18',
    HU18_ADULT = 'hu-18-adult',
    HU18_ADULT_UC = 'hu-18-adult-uc',
    UNKNOWN = 'unknown',
}

export enum EventParentalControlRating {
    HU_GENERAL = 'hu-gy',
    HU_UNRATED = 'hu-unrated',
    HU6 = 'hu-6',
    HU12 = 'hu-12',
    HU16 = 'hu-16',
    HU18 = 'hu-18',
    HU18_ADULT = 'hu-18-adult',
    HU18_ADULT_UC = 'hu-18-adult-uc',
    UNKNOWN = 'unknown',
}

export type VideoItem = {
    channelId: string | undefined;
    channelTitle: string | undefined;
    channelNumber: number | undefined;
    drmId: string | undefined;
    isFreeToAir: boolean | undefined;
    playbackUrls: Array<{
        url: string;
        type: StreamTypes;
    }>;
    id: string;
    title: string | undefined;
    seriesId: string | undefined;
    seriesTitle: string | undefined;
    seasonNumber?: string | number | undefined;
    episodeNumber?: string | number | undefined;
    episodeTitle?: string | number | undefined;
    parentalRating?: EventParentalControlRating | undefined;
    countires?: string | null;
    contentId?: string;
    originalProgram?: EpgProgram | RBProgram | VodEditorial | RecordingItem;
    timeshiftAllowed: boolean;
    startoverAllowed: boolean;
    trickModesControl: TrickModesByPlaybackType;
    guardTime: number;
    guardTimeEnd: number;
    startTime: number | undefined;
    endTime: number | undefined;
    duration: number | null | undefined;
    oohConfig: { [key in StreamTypes]: '0' | '1' | undefined } | undefined;
    sourceConfig?: BitmovinPlayerSourceConfig;
} & ClientBlackoutControlFlags;

export function isSeriesProgram(
    maybeSeriesProgram: any,
): maybeSeriesProgram is RBProgram | RBEditorial | RecordingItem {
    return !!(
        maybeSeriesProgram &&
        (maybeSeriesProgram.seriesRef || maybeSeriesProgram.seriesId)
    );
}
